import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supervisorAPI } from '../../utils/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, ChevronLeft, ChevronRight, Camera, 
  CheckCircle2, RefreshCw, X, MapPin, Image, Phone
} from 'lucide-react';

// Helper function to compress image before upload
const compressImage = async (dataUrl, maxWidth = 1000, quality = 0.7) => {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;
      
      // Scale down if larger than maxWidth
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      
      const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
      resolve(compressedDataUrl);
    };
    img.onerror = () => reject(new Error('Failed to load image for compression'));
    img.src = dataUrl;
  });
};

// Helper to convert data URL to File
const dataURLtoFile = (dataUrl, filename) => {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

const SupervisorPendingAttendance = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [parksData, setParksData] = useState([]);
  const [summary, setSummary] = useState({ total_absent: 0, corrected: 0, pending: 0 });
  const [lastDay, setLastDay] = useState(0);
  const [editWindow, setEditWindow] = useState({ today: '', earliest_editable: '', from_date: null, valid_until: null, permission_active: false });
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  
  // Photo upload modal
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('Present');
  const fileInputRef = useRef(null);
  
  // Expanded parks
  const [expandedParks, setExpandedParks] = useState({});
  
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/supervisor/login');
      return;
    }
    fetchPendingAttendance();
  }, [user, authLoading, navigate, currentMonth]);

  const fetchPendingAttendance = async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      // Check if user and supervisor_id are valid
      if (!user?.supervisor_id) {
        console.error('No supervisor_id in user context:', user);
        toast.error('Session error - please login again');
        navigate('/supervisor/login');
        return;
      }
      
      const response = await supervisorAPI.getPendingAttendance(user.supervisor_id, currentMonth);
      setParksData(response.data.parks || []);
      setSummary(response.data.summary || { total_absent: 0, corrected: 0, pending: 0 });
      setLastDay(response.data.last_day || 0);
      setEditWindow(response.data.edit_window || { today: '', earliest_editable: '', from_date: null, valid_until: null, permission_active: false });
      
      // Auto-expand first 3 parks
      const autoExpand = {};
      (response.data.parks || []).slice(0, 3).forEach(p => {
        autoExpand[p.park_id] = true;
      });
      setExpandedParks(autoExpand);
      
      if (isManualRefresh) {
        toast.success('Data refreshed');
      }
    } catch (error) {
      console.error('Error fetching pending attendance:', error, 'User:', user);
      toast.error('Failed to load attendance data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchPendingAttendance(true);
  };

  const changeMonth = (delta) => {
    const [year, month] = currentMonth.split('-').map(Number);
    let newMonth = month + delta;
    let newYear = year;
    
    if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    } else if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    }
    
    // Don't allow future months
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMon = now.getMonth() + 1;
    
    if (newYear > currentYear || (newYear === currentYear && newMonth > currentMon)) {
      toast.info('Cannot view future months');
      return;
    }
    
    setCurrentMonth(`${newYear}-${String(newMonth).padStart(2, '0')}`);
  };

  const togglePark = (parkId) => {
    setExpandedParks(prev => ({
      ...prev,
      [parkId]: !prev[parkId]
    }));
  };

  const openUploadModal = (park, worker, day, cellData) => {
    const [year, mon] = currentMonth.split('-').map(Number);
    const dateStr = `${year}-${String(mon).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    setSelectedCell({
      park_id: park.park_id,
      park_name: park.park_name,
      employee_id: worker.employee_id,
      employee_name: worker.employee_name,
      category: worker.category,
      day: day,
      date: dateStr,
      attendance_id: cellData.id || `no_record_${park.park_id}_${worker.employee_id}_${dateStr}`,
      no_record: cellData.no_record || false,
      status: cellData.status
    });
    setPhotoPreview(null);
    setSelectedStatus('Present');
    setShowUploadModal(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCorrectAttendance = async () => {
    if (!selectedCell) return;
    
    if (selectedStatus === 'Present' && !photoPreview) {
      toast.error('Please select a photo');
      return;
    }
    
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('attendance_id', selectedCell.attendance_id);
      formData.append('supervisor_id', user.supervisor_id);
      formData.append('status', selectedStatus);
      
      if (selectedStatus === 'Present' && photoPreview) {
        // Compress the image before uploading
        let compressedImage = photoPreview;
        try {
          compressedImage = await compressImage(photoPreview, 1000, 0.7);
          console.log('[CorrectionPhoto] Compressed from', photoPreview.length, 'to', compressedImage.length);
        } catch (compressErr) {
          console.warn('[CorrectionPhoto] Compression failed, using original:', compressErr);
        }
        
        // Convert compressed data URL to File
        const compressedFile = dataURLtoFile(compressedImage, `correction_${Date.now()}.jpg`);
        formData.append('photo', compressedFile);
      }
      
      // If no_record, send additional fields
      if (selectedCell.no_record || selectedCell.attendance_id.startsWith('no_record_')) {
        formData.append('employee_id', selectedCell.employee_id);
        formData.append('park_id', selectedCell.park_id);
        formData.append('date', selectedCell.date);
        formData.append('employee_name', selectedCell.employee_name);
        formData.append('category', selectedCell.category || '');
      }
      
      await supervisorAPI.correctAttendance(formData);
      
      toast.success('Attendance updated successfully');
      setShowUploadModal(false);
      setSelectedCell(null);
      setPhotoPreview(null);
      
      fetchPendingAttendance(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update attendance');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const monthName = new Date(currentMonth + '-01').toLocaleString('default', { month: 'long', year: 'numeric' });
  
  // Generate day headers
  const dayHeaders = [];
  for (let d = 1; d <= lastDay; d++) {
    dayHeaders.push(d);
  }

  const getCellClass = (cellData) => {
    if (!cellData) return 'bg-slate-100';
    
    // Admin-edited cells - highlight with darker colors
    // Check this FIRST, before corrected status
    const isAdminEdited = cellData.manually_edited === true;
    if (isAdminEdited) {
      if (cellData.status === 'A') {
        return 'bg-red-300 text-red-900 cursor-pointer hover:bg-red-400 border-2 border-red-500';
      } else if (cellData.status === 'P') {
        return 'bg-blue-200 text-blue-800 cursor-pointer hover:bg-blue-300 border-2 border-blue-500';
      }
    }
    
    if (cellData.corrected) {
      return 'bg-blue-100 text-blue-700';
    }
    
    switch (cellData.status) {
      case 'P':
        return 'bg-green-100 text-green-700';
      case 'A':
        return 'bg-red-100 text-red-700 cursor-pointer hover:bg-red-200';
      case '-':
        return 'bg-yellow-100 text-yellow-700 cursor-pointer hover:bg-yellow-200';
      default:
        return 'bg-slate-100';
    }
  };

  const canCorrect = (cellData, dateStr) => {
    if (!cellData) return false;
    
    // Allow re-correction for admin-edited cells
    const isAdminEdited = cellData.manually_edited === true;
    
    // Block if already corrected by supervisor (unless admin edited after)
    if (cellData.corrected && !isAdminEdited) return false;
    
    // Only allow Absent, Not marked, or admin-edited cells
    if (cellData.status !== 'A' && cellData.status !== '-' && !isAdminEdited) return false;
    
    // Check if within 3-day edit window
    if (!editWindow.earliest_editable) return true; // fallback
    
    if (dateStr >= editWindow.earliest_editable) {
      return true; // Within 3-day window
    }
    
    // Check if admin granted permission for this month (from 1st of month to valid_until)
    if (editWindow.permission_active && editWindow.from_date && editWindow.valid_until) {
      // Date must be within the permission range: from_date <= dateStr <= valid_until
      if (dateStr >= editWindow.from_date && dateStr <= editWindow.valid_until) {
        return true;
      }
    }
    
    return false; // Older than 3 days and no valid permission
  };

  const isDateEditable = (dateStr) => {
    if (!editWindow.earliest_editable) return true;
    if (dateStr >= editWindow.earliest_editable) return true;
    // Check if permission active and date is within the permission range (from_date to valid_until)
    if (editWindow.permission_active && editWindow.from_date && editWindow.valid_until) {
      if (dateStr >= editWindow.from_date && dateStr <= editWindow.valid_until) return true;
    }
    return false;
  };

  const handleCellClick = async (park, worker, day, cellData, dateStr, editable) => {
    // Only allow clicking on Absent ('A') or Not marked ('-') cells
    // Also allow clicking on already corrected cells OR admin-edited cells for re-correction
    const isAdminEdited = cellData?.manually_edited === true;
    const canCorrect = cellData?.status === 'A' || cellData?.status === '-' || cellData?.corrected || isAdminEdited;
    if (!canCorrect) return;
    
    // Block corrections only for future dates (allow today) - use local date
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    if (dateStr > today) {
      toast.error('Corrections can only be made for today or past dates, not future');
      return;
    }
    
    if (editable) {
      // Open correction modal
      openUploadModal(park, worker, day, cellData);
    } else {
      // Show permission request dialog
      if (window.confirm(`This date (${dateStr}) is older than 3 days.\n\nWould you like to request admin permission to correct attendance?`)) {
        try {
          await supervisorAPI.requestCorrectionPermission(user.supervisor_id, dateStr);
          toast.success('Permission request sent to admin');
        } catch (error) {
          toast.error('Failed to send request');
        }
      }
    }
  };

  if (loading && !refreshing) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-orange-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-orange-600 text-white p-4 shadow-md sticky top-0 z-20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/supervisor/dashboard" className="p-2 hover:bg-white/20 rounded-lg">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="text-xl font-bold uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Pending Attendance
              </h1>
              <p className="text-sm opacity-90">Click on A or - to correct attendance</p>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 bg-white/20 rounded-lg hover:bg-white/30 disabled:opacity-50"
            data-testid="refresh-pending-btn"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="p-4 pb-24">
        {/* Summary Card - Only Corrections */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <div className="flex items-center justify-center gap-3">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">{summary.corrected}</div>
              <div className="text-sm text-slate-500 font-semibold">CORRECTIONS DONE</div>
            </div>
          </div>
          {editWindow.permission_active && editWindow.from_date && editWindow.valid_until && (
            <div className="mt-3 text-center text-sm text-purple-600 bg-purple-50 rounded-lg p-2">
              ✓ Permission granted to edit {editWindow.from_date} to {editWindow.valid_until}
            </div>
          )}
        </div>

        {/* Month Navigator */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => changeMonth(-1)}
              className="p-2 hover:bg-slate-100 rounded-lg"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h2 className="font-bold text-lg">{monthName}</h2>
            <button
              onClick={() => changeMonth(1)}
              className="p-2 hover:bg-slate-100 rounded-lg"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          
          {/* Legend */}
          <div className="flex items-center justify-center gap-4 mt-3 text-xs flex-wrap">
            <div className="flex items-center gap-1">
              <div className="w-6 h-6 bg-green-100 rounded flex items-center justify-center text-green-700 font-bold">P</div>
              <span>Present</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-6 h-6 bg-red-100 rounded flex items-center justify-center text-red-700 font-bold">A</div>
              <span>Absent</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-6 h-6 bg-yellow-100 rounded flex items-center justify-center text-yellow-700 font-bold">-</div>
              <span>Not Visited</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center text-blue-700 font-bold">P</div>
              <span>Corrected</span>
            </div>
          </div>
        </div>

        {/* Parks with Workers */}
        {parksData.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <h3 className="font-bold text-lg">No Parks Assigned</h3>
            <p className="text-slate-500">You don't have any parks assigned</p>
          </div>
        ) : (
          <div className="space-y-4">
            {parksData.map(park => (
              <div key={park.park_id} className="bg-white rounded-xl shadow-sm overflow-hidden">
                {/* Park Header */}
                <button
                  onClick={() => togglePark(park.park_id)}
                  className="w-full p-4 flex items-center justify-between bg-slate-50 hover:bg-slate-100"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <MapPin className="w-4 h-4 text-slate-500" />
                    <span className="font-semibold text-sm">{park.park_name}</span>
                    <span className="text-xs bg-slate-200 px-2 py-0.5 rounded">Zone {park.zone}</span>
                    {park.division && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Div {park.division}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">{park.workers?.length || 0} workers</span>
                    <ChevronRight className={`w-4 h-4 transition-transform ${expandedParks[park.park_id] ? 'rotate-90' : ''}`} />
                  </div>
                </button>
                
                {/* Client Supervisor Contact */}
                {expandedParks[park.park_id] && park.client_supervisor && (
                  <div className="px-4 py-2 bg-green-50 border-b flex items-center gap-3 text-sm">
                    <span className="text-green-700 font-semibold">Client Contact:</span>
                    <span className="font-medium">{park.client_supervisor.name}</span>
                    {park.client_supervisor.mobile && (
                      <a 
                        href={`tel:${park.client_supervisor.mobile}`} 
                        className="inline-flex items-center gap-1 bg-green-600 text-white px-3 py-1 rounded-full text-xs font-semibold hover:bg-green-700"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Phone className="w-3 h-3" />
                        {park.client_supervisor.mobile}
                      </a>
                    )}
                  </div>
                )}
                
                {/* Workers Table */}
                {expandedParks[park.park_id] && park.workers && park.workers.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-800 text-white">
                          <th className="p-2 text-left sticky left-0 bg-slate-800 min-w-[150px]">Worker</th>
                          {dayHeaders.map(d => (
                            <th key={d} className="p-2 text-center min-w-[40px]">{d}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {park.workers.map((worker, idx) => (
                          <tr key={worker.employee_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="p-2 sticky left-0 bg-inherit border-r">
                              <div className="font-semibold text-xs truncate max-w-[140px]" title={worker.employee_name}>
                                {worker.employee_name}
                              </div>
                              <div className="text-xs text-slate-400">{worker.category}</div>
                            </td>
                            {dayHeaders.map(d => {
                              const cellData = worker.attendance?.[String(d)];
                              const [year, mon] = currentMonth.split('-').map(Number);
                              const dateStr = `${year}-${String(mon).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                              const editable = isDateEditable(dateStr);
                              // Allow clicking on Absent, Not marked, OR admin-edited cells (even if present)
                              const isAdminEdited = cellData?.manually_edited === true;
                              const isClickable = (cellData?.status === 'A' || cellData?.status === '-' || isAdminEdited) && (!cellData?.corrected || isAdminEdited);
                              return (
                                <td 
                                  key={d} 
                                  className={`p-1 text-center ${getCellClass(cellData)} ${!editable && isClickable ? 'opacity-50 cursor-pointer' : isClickable ? 'cursor-pointer' : ''}`}
                                  onClick={() => isClickable && handleCellClick(park, worker, d, cellData, dateStr, editable)}
                                  title={!editable && isClickable ? 'Click to request admin permission' : isAdminEdited ? 'Admin edited - click to re-correct' : ''}
                                >
                                  <span className="font-bold text-xs">
                                    {cellData?.status || '-'}
                                  </span>
                                  {cellData?.corrected && !isAdminEdited && (
                                    <CheckCircle2 className="w-3 h-3 mx-auto text-blue-600" />
                                  )}
                                  {isAdminEdited && (
                                    <span className="text-xs font-bold" title="Admin edited - click to re-correct">✎</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Photo Upload Modal */}
      {showUploadModal && selectedCell && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-bold text-lg">Correct Attendance</h3>
              <button
                onClick={() => setShowUploadModal(false)}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4">
              <div className="bg-slate-50 rounded-lg p-3 mb-4">
                <div className="font-semibold">{selectedCell.employee_name}</div>
                <div className="text-sm text-slate-500">{selectedCell.park_name}</div>
                <div className="text-sm text-slate-500">
                  {new Date(selectedCell.date).toLocaleDateString('en-IN', { 
                    weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' 
                  })}
                </div>
                <div className="mt-2">
                  <span className={`text-xs px-2 py-1 rounded font-semibold ${
                    selectedCell.status === 'A' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {selectedCell.status === 'A' ? 'Marked Absent' : 'Not Visited'}
                  </span>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">Select Attendance Status</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedStatus('Present')}
                    className={`flex-1 py-2 px-4 rounded-lg font-semibold border transition-all ${
                      selectedStatus === 'Present'
                        ? 'bg-orange-600 border-orange-600 text-white shadow-sm'
                        : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    Present
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedStatus('Absent')}
                    className={`flex-1 py-2 px-4 rounded-lg font-semibold border transition-all ${
                      selectedStatus === 'Absent'
                        ? 'bg-orange-600 border-orange-600 text-white shadow-sm'
                        : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    Absent
                  </button>
                </div>
              </div>
              
              {selectedStatus === 'Present' ? (
                <>
                  <p className="text-sm text-slate-600 mb-3">
                    Upload a photo as proof to mark this worker as Present:
                  </p>
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    capture="environment"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  
                  {photoPreview ? (
                    <div className="relative mb-4">
                      <img 
                        src={photoPreview} 
                        alt="Preview" 
                        className="w-full h-48 object-cover rounded-lg"
                      />
                      <button
                        onClick={() => {
                          setPhotoPreview(null);
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full h-32 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-orange-500 hover:bg-orange-50 mb-4"
                    >
                      <Camera className="w-8 h-8 text-slate-400" />
                      <span className="text-sm text-slate-500">Tap to take photo or select</span>
                    </button>
                  )}
                </>
              ) : (
                <div className="bg-yellow-50 text-yellow-800 border border-yellow-100 rounded-lg p-4 mb-4 text-sm font-medium">
                  Confirm to mark this worker as Absent. No photo proof is required.
                </div>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="flex-1 px-4 py-3 border border-slate-300 rounded-lg font-semibold hover:bg-slate-50"
                  disabled={uploadingPhoto}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCorrectAttendance}
                  disabled={uploadingPhoto || (selectedStatus === 'Present' && !photoPreview)}
                  className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="submit-correction-btn"
                >
                  {uploadingPhoto ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      {selectedStatus === 'Present' ? 'Mark Present' : 'Mark Absent'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupervisorPendingAttendance;
